// FormulaireProduitPage.js - Page complète pour les formulaires de produits

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../firebaseConfig";
import FormulaireWrapper from "../components/Formulaires/FormulaireWrapper";
import { toast } from "react-toastify";

const FormulaireProduitPage = () => {
  const { orderId, produitId } = useParams();
  const navigate = useNavigate();
  const db = getFirestore(app);
  
  const [loading, setLoading] = useState(true);
  const [dossier, setDossier] = useState(null);
  const [produit, setProduit] = useState(null);
  const [produitIndex, setProduitIndex] = useState(-1);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDossierAndProduit = async () => {
      try {
        setLoading(true);
        
        // Charger le dossier
        const dossierRef = doc(db, "dossiers", orderId);
        const dossierSnap = await getDoc(dossierRef);
        
        if (!dossierSnap.exists()) {
          throw new Error("Dossier non trouvé");
        }
        
        const dossierData = dossierSnap.data();
        setDossier(dossierData);
        
        // Trouver le produit spécifique
        const produits = dossierData.produits || [];
        const index = produits.findIndex(p => p.uuid === produitId || p.productId === produitId);
        
        if (index === -1) {
          throw new Error("Produit non trouvé dans ce dossier");
        }
        
        setProduit(produits[index]);
        setProduitIndex(index);
        
      } catch (err) {
        console.error("❌ Erreur lors du chargement:", err);
        setError(err.message);
        toast.error(`Erreur: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (orderId && produitId) {
      loadDossierAndProduit();
    }
  }, [orderId, produitId, db]);

  const handleNext = () => {
    // Logique après soumission du formulaire
    toast.success("Formulaire enregistré avec succès!");
    
    // Rediriger vers la liste des commandes ou le dashboard
    navigate(`/revendeur/orders/${orderId}`);
  };

  const handleCancel = () => {
    // Retour à la liste des commandes
    navigate(`/revendeur/orders/${orderId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Chargement du formulaire...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={handleCancel}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded transition"
                >
                  Retour aux commandes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!produit) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Produit non trouvé</h2>
          <p className="mt-2 text-gray-600">Le produit demandé n'existe pas dans ce dossier.</p>
          <button
            onClick={handleCancel}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition"
          >
            Retour aux commandes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* En-tête de la page */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Formulaire de contrôle montage et mise en service
              </h1>
              <div className="mt-2 space-y-1 text-sm text-gray-600">
                <p><span className="font-medium">Dossier:</span> {dossier?.orderName || orderId}</p>
                <p><span className="font-medium">Produit:</span> {produit.name}</p>
                <p><span className="font-medium">Destinataire:</span> {dossier?.destinataire_type}</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Annuler
              </button>
            </div>
          </div>
          
          {/* Barre de progression si nécessaire */}
          {dossier?.produits && dossier.produits.length > 1 && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Produit {produitIndex + 1} sur {dossier.produits.length}</span>
                <span>
                  {dossier.produits.filter(p => p.filled).length} / {dossier.produits.length} complétés
                </span>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(dossier.produits.filter(p => p.filled).length / dossier.produits.length) * 100}%` 
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Formulaire principal */}
        <FormulaireWrapper
          produit={produit}
          orderId={orderId}
          index={produitIndex}
          onNext={handleNext}
        />
        
      </div>
    </div>
  );
};

export default FormulaireProduitPage;